import React from "react";
import { i18n } from "../../translate/i18n";
import MasterModal from "./MasterModal";
import { formatContractDueDate } from "../../helpers/formatContractDueDate";
import { formatContractValue } from "../../helpers/formatContractValue";

function CopyRow({ label, value, onCopy, extra }) {
	return (
		<div className="flex items-center gap-3 p-3.5 rounded-lg border border-default-200 bg-default-50 dark:bg-default-800/50 dark:border-default-600">
			<span className="text-sm font-semibold text-default-500 dark:text-default-400 min-w-[3rem] shrink-0">
				{label}
			</span>
			<span className="flex-1 text-sm text-default-800 dark:text-default-100 font-mono break-all select-text">
				{value}
			</span>
			{extra}
			{onCopy && value && value !== "—" && (
				<button
					type="button"
					className="btn-ghost !p-2 shrink-0 text-primary"
					onClick={onCopy}
					title={`Copiar ${label}`}
				>
					<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<rect x="9" y="9" width="13" height="13" rx="2" />
						<path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
					</svg>
				</button>
			)}
		</div>
	);
}

export default function ViewCompanyModal({ company, onClose, formatCnpj, formatDate, onCopy }) {
	if (!company) return null;

	return (
		<MasterModal
			open={!!company}
			onClose={onClose}
			title={i18n.t("master.pages.viewCompany")}
			subtitle={i18n.t("master.pages.viewCompanySubtitle")}
		>
			<div className="mb-7">
				<p className="text-xs font-semibold uppercase tracking-wide text-default-500 mb-2">
					Nome da empresa
				</p>
				<p className="text-xl font-bold text-default-900 dark:text-default-50 m-0">
					{company.name || "—"}
				</p>
			</div>

			<div className="flex flex-col gap-3.5 mb-5">
				<CopyRow
					label="CNPJ"
					value={formatCnpj(company.document)}
					onCopy={() => onCopy(formatCnpj(company.document), "CNPJ")}
				/>
				<CopyRow
					label="E-mail"
					value={company.primaryEmail || "—"}
					onCopy={
						company.primaryEmail
							? () => onCopy(company.primaryEmail, "E-mail")
							: undefined
					}
					extra={
						company.primaryEmail ? (
							<a
								href={`mailto:${company.primaryEmail}`}
								className="btn-ghost !p-2 shrink-0 text-green-600 dark:text-green-400"
								title="Enviar e-mail"
							>
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
									<polyline points="22,6 12,13 2,6" />
								</svg>
							</a>
						) : null
					}
				/>
				<CopyRow
					label="Telefone"
					value={company.primaryPhone || "—"}
					onCopy={
						company.primaryPhone
							? () => onCopy(company.primaryPhone, "Telefone")
							: undefined
					}
				/>
				<CopyRow
					label="Chave da API"
					value={company.apiKey || "—"}
					onCopy={
						company.apiKey ? () => onCopy(company.apiKey, "Chave da API") : undefined
					}
				/>
			</div>

			<div className="mb-5 p-5 rounded-lg border border-default-200 bg-default-100 dark:bg-default-800/40 dark:border-default-600">
				<h3 className="text-xs font-semibold uppercase tracking-wide text-default-500 m-0 mb-3">
					{i18n.t("master.pages.contractSectionTitle")}
				</h3>
				<div className="mb-4">
					<p className="text-xs text-default-500 m-0 mb-1">
						{i18n.t("master.pages.contractValueLabel")}
					</p>
					<p className="text-sm font-semibold text-default-800 dark:text-default-100 m-0">
						{formatContractValue(company.contractValue)}
					</p>
				</div>
				<div className="mb-4">
					<p className="text-xs text-default-500 m-0 mb-1">
						{i18n.t("master.pages.contractDueDateLabel")}
					</p>
					<p className="text-sm font-medium text-default-800 dark:text-default-100 m-0">
						{formatContractDueDate(company.contractDueDate)}
					</p>
				</div>
				<div>
					<p className="text-xs text-default-500 m-0 mb-1">
						{i18n.t("master.pages.contractDetailsLabel")}
					</p>
					<p className="text-sm text-default-800 dark:text-default-100 m-0 whitespace-pre-wrap break-words">
						{company.contractDetails?.trim() || "—"}
					</p>
				</div>
			</div>

			<div className="mb-5 p-5 rounded-lg border border-default-200 bg-default-100 dark:bg-default-800/40 dark:border-default-600">
				<h3 className="text-xs font-semibold uppercase tracking-wide text-default-500 m-0 mb-3">
					{i18n.t("master.editCompany.planSectionTitle")}
				</h3>
				{company.companyPlan ? (
					<>
						<div className="mb-4">
							<p className="text-xs text-default-500 m-0 mb-1">
								{i18n.t("master.pages.viewPlanNameLabel")}
							</p>
							<p className="text-sm font-semibold text-default-800 dark:text-default-100 m-0">
								{company.companyPlan.planName || "—"}
								<span className="ml-2 text-xs font-medium text-default-500">
									(
									{company.companyPlan.planId
										? i18n.t("master.editCompany.planModeCatalog")
										: i18n.t("master.editCompany.planModeCustom")}
									)
								</span>
							</p>
						</div>
						<div className="flex flex-wrap gap-5">
							{[
								[
									i18n.t("master.editCompany.planMonthlyTokensLabel"),
									company.companyPlan.monthlyTokens ?? 0,
								],
								[
									i18n.t("master.editCompany.planUsedTokens"),
									company.companyPlan.usedTokens ?? 0,
								],
								[
									i18n.t("master.editCompany.planExtraTokens"),
									company.companyPlan.extraTokens ?? 0,
								],
								[
									i18n.t("master.editCompany.planRemainingTokens"),
									company.companyPlan.remainingTokens ?? 0,
								],
							].map(([label, val]) => (
								<div key={label}>
									<p className="text-xs text-default-500 m-0">{label}</p>
									<p className="text-lg font-bold text-default-900 dark:text-default-50 m-0">
										{val}
									</p>
								</div>
							))}
						</div>
					</>
				) : (
					<p className="text-sm text-default-500 m-0">
						{i18n.t("master.editCompany.planNoActive")}
					</p>
				)}
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="flex-1 min-w-[180px] p-5 rounded-lg border border-default-200 bg-default-100 dark:bg-default-800/40 dark:border-default-600">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-default-500 m-0 mb-3">
						Cadastro
					</h3>
					<p className="text-sm font-medium text-default-800 dark:text-default-100 m-0">
						{formatDate(company.createdAt)}
					</p>
					<p className="text-xs font-medium text-default-500 mt-3 mb-0">Status</p>
					<p
						className={`text-sm font-semibold mt-1 mb-0 ${
							company.isActive !== false
								? "text-green-700 dark:text-green-400"
								: "text-danger dark:text-orange-400"
						}`}
					>
						{company.isActive !== false ? "Ativo" : "Inativo"}
					</p>
				</div>
				<div className="flex-[1.3] min-w-[240px] p-5 rounded-lg border border-default-200 bg-default-100 dark:bg-default-800/40 dark:border-default-600">
					<h3 className="text-xs font-semibold uppercase tracking-wide text-default-500 m-0 mb-3">
						Uso do sistema
					</h3>
					<div className="flex flex-wrap gap-5">
						{[
							["Usuários", company.userCount ?? 0],
							["Limite de usuários", company.userLimit ?? "—"],
							["Conexões", company.connectionCount ?? 0],
							["Limite de conexões", company.connectionLimit ?? "—"],
						].map(([label, val]) => (
							<div key={label}>
								<p className="text-xs text-default-500 m-0">{label}</p>
								<p className="text-lg font-bold text-default-900 dark:text-default-50 m-0">
									{val}
								</p>
							</div>
						))}
					</div>
				</div>
			</div>
		</MasterModal>
	);
}
